import { useContext } from "react";
import { Autocomplete, Avatar, TextField, Typography } from "@mui/material";
import { CacheContext } from "../../../Context/cacheContext";
import { AutocompleteUser } from "../../../Models/Models";
import { Vocabulary } from "../../../Utils/Vocabulary";
import SidebarCard from "./SidebarCard";
import AssociateVehicleButton from "./AssociateVehicleButton";
import styles from "./Components.module.css";

interface RightSidebarProps {
  lead: any;
  isAdmin: boolean;
  statusId: number | undefined;
  assignedUserId: number | null | undefined;
  setAssignedUserId: (id: number | null) => void;
  onConvert: () => void;
  refetchLead: () => void;
}

export default function RightSidebar({
  lead,
  isAdmin,
  statusId,
  assignedUserId,
  setAssignedUserId,
  onConvert,
  refetchLead,
}: RightSidebarProps) {
  const { cache } = useContext(CacheContext);

  const assignedUsers: AutocompleteUser[] =
    cache.users?.map((u: any) => ({ id: u.id, name: u.nume, nume: u.nume })) || [];

  const assignedUser = assignedUsers.find((u) => u.id === assignedUserId);

  const canceledStatusId = cache.clientMessagesStatus?.find(
    (s: any) => s.nume === "Anulat"
  )?.id;

  return (
    <div className={styles.rightSidebar}>
      <div className={styles.rightSidebarStack}>

        {/* Ownership */}
        <SidebarCard title={Vocabulary.ownership || "Proprietar"} color="#64748b" showDot={false}>
          {assignedUser ? (
            <div className={styles.ownerRow} style={{ marginBottom: isAdmin ? 12 : 0 }}>
              <Avatar style={{ width: 32, height: 32, fontSize: 13, backgroundColor: "#FF6B1A", fontWeight: 700 }}>
                {(assignedUser.nume || "?").charAt(0).toUpperCase()}
              </Avatar>
              <div>
                <Typography variant="body2" className={styles.ownerName}>
                  {assignedUser.nume}
                </Typography>
                <Typography variant="caption" color="text.disabled" className={styles.ownerCaption}>
                  {Vocabulary.assignedAgent}
                </Typography>
              </div>
            </div>
          ) : (
            <Typography variant="body2" color="text.disabled" className={styles.unassignedText} style={{ marginBottom: isAdmin ? 10 : 0 }}>
              {Vocabulary.assignToAUser}
            </Typography>
          )}
          {isAdmin && (
            <Autocomplete
              size="small"
              options={assignedUsers}
              value={assignedUsers.find((u) => u.id === assignedUserId) || null}
              getOptionLabel={(o) => o.name || o.nume || ""}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(_, val) => setAssignedUserId(val?.id ?? null)}
              renderInput={(params) => (
                <TextField {...params} label={Vocabulary.assignToAUser} size="small" />
              )}
            />
          )}
        </SidebarCard>

        {/* Conversion actions — cancel reason + associate vehicle (convert is in header) */}
        {(lead.cancel_reason && statusId === canceledStatusId || !lead.vehicle) && (
          <SidebarCard title={Vocabulary.conversionActions || "Acțiuni conversie"} color="#16a34a" showDot={false}>
            {lead.cancel_reason && statusId === canceledStatusId && (
              <div className={styles.cancelBox} style={{ marginBottom: !lead.vehicle ? 8 : 0 }}>
                <Typography variant="caption" className={styles.cancelLabel}>
                  {Vocabulary.cancelReason || "Motiv anulare"}
                </Typography>
                <Typography variant="caption" color="error.main" className={styles.cancelText}>
                  {lead.cancel_reason}
                </Typography>
              </div>
            )}
            {!lead.vehicle && (
              <AssociateVehicleButton leadId={lead.id} onAssociated={refetchLead} />
            )}
          </SidebarCard>
        )}

      </div>
    </div>
  );
}
